import moment from 'moment'

// OTC-3144. "X/Y resources available" on a booking row, counted the way the
// autoblocking module judges capacity for a slot (calculateAvailableCapacity
// and freeUnitsForCandidate in backend/autoblocking-module). Per resource slot:
//   1. its shift must contain the slot's raw event window (resourceCoversWindow
//      with the default raw coverage window),
//   2. it must apply to the slot's event (apply_to_all_events, or the event is
//      in its linked_games),
//   3. its free units are supported_event_count minus the PEAK number of its
//      own holds overlapping the window (sweep line, inclusive edges when
//      include_start_end_time_in_blocking is on), clamped at 0, and 0 once it
//      holds anything when saturate_on_first_assignment is on.
// total is the summed supported_event_count of the resources that pass 1 and
// 2; available is the summed free units. assigned counts this slot's own
// links on any resource.
// Events that allocate by resource category are also counted per required
// category (see getSlotResourceAvailability).
// Group resource settings (options, from the calendar lambda's
// resource_settings), as the engine's loadGroupSettings applies them:
//   - requireFullBufferCoverage: the shift must also cover the slot's event
//     pre and post buffers (getCoverageWindow). Concurrency still uses the raw
//     slot window.
//   - crossGameBuffers false: a hold from a different event occupies the
//     resource only for that booking's raw slot times, not its buffered hold
//     (countConcurrentFromHolds' within-game-buffers recount).

function datePart(value) {
  if (!value) return ''
  return typeof value === 'string' ? value.substring(0, 10) : moment(value).format('YYYY-MM-DD')
}

// Stored times are venue wall clock with no zone. They are compared as plain
// clock positions in UTC (no daylight saving), as the engine does on its UTC
// Lambda. The browser zone or the group zone would move a nonexistent
// spring-forward time (02:30 to 03:30) and change the count.
function toMs(date, time) {
  const m = moment.utc(`${datePart(date)} ${time}`, 'YYYY-MM-DD HH:mm:ss')
  return m.isValid() ? m.valueOf() : null
}

// Hold datetimes arrive as 'YYYY-MM-DD HH:mm:ss[.ffffff]' from JSON_OBJECT.
function datetimeMs(value) {
  if (!value) return null
  const text = typeof value === 'string' ? value : moment(value).format('YYYY-MM-DD HH:mm:ss')
  const m = moment.utc(text, 'YYYY-MM-DD HH:mm:ss')
  return m.isValid() ? m.valueOf() : null
}

function parseList(value) {
  if (!value) return []
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }
  return Array.isArray(value) ? value : []
}

export function resourceAppliesToEvent(resource, gameId) {
  if (Number(resource.apply_to_all_events) === 1 || resource.apply_to_all_events === true) return true
  return parseList(resource.linked_games).some(g => String(g.id) === String(gameId))
}

// Interval a hold occupies for a slot of event `gameId`: the buffered hold
// (time_assigned..time_freed_up), or, with cross-event buffers off, the raw
// slot times of a hold from a different event. Holds with no event or raw
// times stay buffered, as in the engine.
function holdInterval(h, gameId, crossGameBuffers) {
  const otherEvent = crossGameBuffers === false && gameId != null && h.game_id != null &&
    String(h.game_id) !== String(gameId)
  if (otherEvent && h.booking_date && h.start_time && h.end_time) {
    const rawStart = toMs(h.booking_date, h.start_time)
    const rawEnd = toMs(h.end_date || h.booking_date, h.end_time)
    if (rawStart !== null && rawEnd !== null) return { assigned: rawStart, freed: rawEnd }
  }
  return { assigned: datetimeMs(h.time_assigned), freed: datetimeMs(h.time_freed_up) }
}

// Peak concurrency of holds inside [start, end]. Back-to-back holds count as
// concurrent only when inclusive (starts sort before ends at the same instant).
// gameId and crossGameBuffers select each hold's interval (see holdInterval).
export function peakConcurrentHolds(holds, start, end, inclusive, gameId = null, crossGameBuffers = true) {
  const events = []
  for (const h of holds) {
    const { assigned, freed } = holdInterval(h, gameId, crossGameBuffers)
    if (assigned === null || freed === null) continue
    const overlaps = inclusive
      ? assigned <= end && freed >= start
      : assigned < end && freed > start
    if (!overlaps) continue
    events.push({ time: Math.max(assigned, start), type: 'start' })
    events.push({ time: Math.min(freed, end), type: 'end' })
  }
  events.sort((a, b) => {
    if (a.time === b.time) {
      if (inclusive) return a.type === 'start' ? -1 : 1
      return a.type === 'end' ? -1 : 1
    }
    return a.time - b.time
  })
  let current = 0
  let peak = 0
  for (const e of events) {
    if (e.type === 'start') {
      current++
      if (current > peak) peak = current
    } else {
      current--
    }
  }
  return peak
}

// Capacity one resource slot offers a booking window: null when it does not
// serve the window (shift does not contain the coverage window, or the
// resource does not apply to the event), else its supported count and free
// units. Coverage uses [coverStart, coverEnd]; concurrency the raw [start, end].
function resourceCapacity(resource, holds, w, gameId, crossGameBuffers) {
  const shiftStart = toMs(resource.start_date, resource.start_time)
  const shiftEnd = toMs(resource.end_date, resource.end_time)
  if (shiftStart === null || shiftEnd === null) return null
  if (shiftStart > w.coverStart || shiftEnd < w.coverEnd) return null
  if (!resourceAppliesToEvent(resource, gameId)) return null

  const supported = Number(resource.supported_event_count) || 0
  const inclusive = Number(resource.include_start_end_time_in_blocking) === 1 || resource.include_start_end_time_in_blocking === true
  const peak = peakConcurrentHolds(holds, w.start, w.end, inclusive, gameId, crossGameBuffers)
  const saturated = (Number(resource.saturate_on_first_assignment) === 1 || resource.saturate_on_first_assignment === true) && peak > 0
  return { supported, free: saturated ? 0 : Math.max(0, supported - peak) }
}

// Resource count a requirement asks of this slot, as the engine resolves it:
// fixed requirements use required_count; tiered ones use the tier matching a
// booked slot's group_size (0 when no tier matches) or, for an unbooked slot
// (group_size 0), the smallest tier (resolveRequirementForGroupSize and
// resolveMinimumRequirement in backend/autoblocking-module).
export function resolveRequiredCount(requirement, groupSize) {
  const tiers = parseList(requirement.tiers)
  if (requirement.allocation_type !== 'tiered' || tiers.length === 0) {
    return Number(requirement.required_count) || 0
  }
  const size = Number(groupSize) || 0
  if (size > 0) {
    const tier = tiers.find(t => size >= Number(t.min_group_size) && size <= Number(t.max_group_size))
    return tier ? Number(tier.required_count) || 0 : 0
  }
  return tiers.reduce((min, t) => Math.min(min, Number(t.required_count) || 0), Infinity)
}

// categoryRequirements: the event's active requirements as sent by
// console-get-bookings-for-calendar (category_requirements rows for this
// game_id: resource_category_id, category_name, required_count,
// allocation_type, tiers). When present, the event is counted the way
// checkCategoryResourceAvailability counts it: per required category, from
// resources in that category only, and `categories` lists one
// { id, name, available, total } per requirement that needs at least one
// resource for this slot (the engine skips the rest). Without requirements
// the engine uses standard counting, and `categories` is null.
const MINUTE_MS = 60 * 1000

// options: { crossGameBuffers (default true), requireFullBufferCoverage
// (default false) }, the group's resource settings. Buffers come from the
// row's pre_buffer_minutes and buffer_minutes (the event's, as the engine reads them).
export function getSlotResourceAvailability(item, resourceSlots, categoryRequirements = [], options = {}) {
  const opts = options || {}
  const crossGameBuffers = opts.crossGameBuffers !== false
  const requireFullBufferCoverage = opts.requireFullBufferCoverage === true
  const requirements = Array.isArray(categoryRequirements) ? categoryRequirements : []
  const categories = requirements.length > 0
    ? requirements
      .filter(r => resolveRequiredCount(r, item && item.group_size) > 0)
      .map(r => ({ id: r.resource_category_id, name: r.category_name, available: 0, total: 0 }))
    : null
  const empty = { assigned: 0, available: 0, total: 0, categories }
  if (!item || !item.booking_date || !item.start_time || !item.end_date || !item.end_time) return empty
  const start = toMs(item.booking_date, item.start_time)
  const end = toMs(item.end_date, item.end_time)
  if (start === null || end === null) return empty
  const pre = requireFullBufferCoverage ? (Number(item.pre_buffer_minutes) || 0) * MINUTE_MS : 0
  const post = requireFullBufferCoverage ? (Number(item.buffer_minutes) || 0) * MINUTE_MS : 0
  const slotWindow = { start, end, coverStart: start - pre, coverEnd: end + post }

  let assigned = 0
  let available = 0
  let total = 0
  for (const resource of resourceSlots || []) {
    const holds = parseList(resource.assignment)
    for (const h of holds) {
      if (h.booking_slot_id === item.id) assigned++
    }

    const capacity = resourceCapacity(resource, holds, slotWindow, item.game_id, crossGameBuffers)
    if (!capacity) continue
    total += capacity.supported
    available += capacity.free
    if (categories && resource.resource_category_id != null) {
      for (const c of categories) {
        if (String(c.id) !== String(resource.resource_category_id)) continue
        c.total += capacity.supported
        c.available += capacity.free
      }
    }
  }
  return { assigned, available, total, categories }
}
